import React from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { TextField } from '@mui/material';
import ReadOnlyField from './readonly-field';
import { DateUtils } from '../../utils';

interface DateFieldProps<T extends FieldValues> {
  name: Path<T>;
  control: Control<T>;
  label: string;
  isReadOnly: boolean;
  required?: boolean;
  fullWidth?: boolean;
  showTime?: boolean;
}

const DateField = <T extends FieldValues>({
  name,
  control,
  label,
  isReadOnly,
  required = false,
  fullWidth = true,
  showTime = false,
}: DateFieldProps<T>): React.ReactElement => {
  const formatDateForDisplay = (value: string | number | boolean): string => {
    if (!value || typeof value !== 'string') return '';
    return showTime ? DateUtils.toDisplayDateTime(value) : DateUtils.toDisplayDate(value);
  };

  if (isReadOnly) {
    return (
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <ReadOnlyField
            label={label}
            value={field.value || ''}
            formatValue={formatDateForDisplay}
          />
        )}
      />
    );
  }

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          type="date"
          fullWidth={fullWidth}
          label={label}
          value={DateUtils.toDateInputFormat(field.value)}
          onChange={e => {
            const isoDate = DateUtils.fromDateInputFormat(e.target.value);
            field.onChange(isoDate);
          }}
          error={!!fieldState.error}
          helperText={fieldState.error?.message}
          required={required}
          InputLabelProps={{
            shrink: true,
          }}
        />
      )}
    />
  );
};

export default DateField;
